import {
  getAiConversationLabels,
  isAiConversationType,
} from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'
import { CONVERSATION_TYPE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

import { MessengerParticipant, MessengerThread, PARTICIPANT_CONFIG } from './types'

export const getAiThreadId = (conversationId: string) => `ai:${conversationId}`

// Guardian contract (MESSENGER_THREADS): the guardian conversation is returned among the patient's
// conversations but carries the guardian's lyra id as its patient_lyra_id — that's how we identify it.
export const getThreadParticipant = (
  conversation: ConversationObject,
  guardianLyraId?: string | null,
): MessengerParticipant => {
  if (isAiConversationType(conversation.conversation_attributes?.type)) {
    return MessengerParticipant.AI
  }
  if (guardianLyraId && conversation.patient_lyra_id === guardianLyraId) {
    return MessengerParticipant.GUARDIAN
  }
  return MessengerParticipant.PATIENT
}

export const getThreadUnreadCount = (
  conversation: ConversationObject | null,
  participant: MessengerParticipant,
): number => {
  if (!conversation) {
    return 0
  }
  const attrs = conversation.conversation_attributes
  if (participant === MessengerParticipant.AI) {
    return attrs?.unread_ai_coach_messages_provider_count ?? 0
  }
  return attrs?.unread_provider_messages_count ?? 0
}

export const getTabLabel = (
  participant: MessengerParticipant,
  patientFirstName: string,
  guardianFirstName: string,
  hasGuardianThread: boolean,
  conversationType?: CONVERSATION_TYPE,
  hasMultipleAiTypes = false,
): string => {
  switch (participant) {
    case MessengerParticipant.GUARDIAN:
      return `${guardianFirstName || 'Caregiver'} (caregiver)`
    case MessengerParticipant.AI:
      return getAiConversationLabels(conversationType, hasMultipleAiTypes).chatTitle
    default:
      return hasGuardianThread ? `${patientFirstName} (teen)` : patientFirstName
  }
}

type BuildMessengerThreadsArgs = {
  clientId: string
  patientAndGuardianConversations: ConversationObject[]
  aiConversations: ConversationObject[]
  eligibleParticipants: MessengerParticipant[]
  patientFirstName: string
  guardianFirstName: string
  patientFullName: string
  guardianFullName: string
  guardianLyraId?: string | null
}

const compareAiConversations = (a: ConversationObject, b: ConversationObject): number => {
  const aOrder = a.conversation_attributes?.type === CONVERSATION_TYPE.MANAGER_COACHING_AI ? 0 : 1
  const bOrder = b.conversation_attributes?.type === CONVERSATION_TYPE.MANAGER_COACHING_AI ? 0 : 1
  return aOrder - bOrder || (a.conversation_id ?? '').localeCompare(b.conversation_id ?? '')
}

export const buildMessengerThreads = ({
  clientId,
  patientAndGuardianConversations,
  aiConversations,
  eligibleParticipants,
  patientFirstName,
  guardianFirstName,
  patientFullName,
  guardianFullName,
  guardianLyraId,
}: BuildMessengerThreadsArgs): MessengerThread[] => {
  const conversationsByParticipant = new Map<MessengerParticipant, ConversationObject>()
  patientAndGuardianConversations.forEach((conversation) => {
    const participant = getThreadParticipant(conversation, guardianLyraId)
    if (!conversationsByParticipant.has(participant)) {
      conversationsByParticipant.set(participant, conversation)
    }
  })

  const hasGuardianThread = eligibleParticipants.includes(MessengerParticipant.GUARDIAN)
  const participantThreads = eligibleParticipants
    .filter((participant) => {
      const config = PARTICIPANT_CONFIG[participant]
      return !config.requiresExistingConversation || conversationsByParticipant.has(participant)
    })
    .map((participant): MessengerThread => {
      const config = PARTICIPANT_CONFIG[participant]
      const conversation = conversationsByParticipant.get(participant) ?? null
      return {
        id: participant,
        participant,
        conversationType: CONVERSATION_TYPE.PROVIDER,
        conversationId: conversation?.conversation_id ?? null,
        conversation,
        recipientId: participant === MessengerParticipant.GUARDIAN ? (guardianLyraId ?? '') : clientId,
        recipientName: participant === MessengerParticipant.GUARDIAN ? guardianFullName : patientFullName,
        tabLabel: getTabLabel(participant, patientFirstName, guardianFirstName, hasGuardianThread),
        unreadCount: config.showUnreadBadge ? getThreadUnreadCount(conversation, participant) : 0,
        canSendMessages: config.canSendMessages,
        showInputActions: config.showInputActions,
        disableDrafts: config.disableDrafts,
      }
    })

  const aiWithIds = aiConversations.filter(
    (conversation) => typeof conversation.conversation_id === 'string' && conversation.conversation_id.length > 0,
  )
  const aiTypes = new Set(
    aiWithIds.map((conversation) => conversation.conversation_attributes?.type).filter(isAiConversationType),
  )
  const hasMultipleAiTypes = aiTypes.size > 1
  const aiConfig = PARTICIPANT_CONFIG[MessengerParticipant.AI]
  const aiThreads = [...aiWithIds].sort(compareAiConversations).flatMap((conversation): MessengerThread[] => {
    const conversationType = conversation.conversation_attributes?.type
    if (!isAiConversationType(conversationType)) {
      return []
    }
    return [
      {
        id: getAiThreadId(conversation.conversation_id),
        participant: MessengerParticipant.AI,
        conversationType,
        conversationId: conversation.conversation_id,
        conversation,
        recipientId: clientId,
        recipientName: patientFullName,
        tabLabel: getTabLabel(
          MessengerParticipant.AI,
          patientFirstName,
          guardianFirstName,
          hasGuardianThread,
          conversationType,
          hasMultipleAiTypes,
        ),
        unreadCount: aiConfig.showUnreadBadge ? getThreadUnreadCount(conversation, MessengerParticipant.AI) : 0,
        canSendMessages: aiConfig.canSendMessages,
        showInputActions: aiConfig.showInputActions,
        disableDrafts: aiConfig.disableDrafts,
      },
    ]
  })

  return [...participantThreads, ...aiThreads]
}
